import React, { useRef, useState } from "react";
import { Alert, Button, Col, Empty, Row, Space, Tag, Typography } from "antd";
import { OrderVersionDto } from "../../../interfaces/OrderDetail";
import { useOrderDetail } from "./hooks/useOrderDetail";
import { AnchorPlanViewer } from "./AnchorPlanViewer";
import { OrderDetailPanelSkeleton } from "./OrderDetailPanelSkeleton";
import { OrderSummaryCard } from "./OrderSummaryCard";
import { QuoteVersionTabs } from "./QuoteVersionTabs";
import { ImportantComments } from "./ImportantComments";
import { CommentResponseInputHandle } from "./CommentResponseInput";

interface OrderDetailRecord {
  sn: number;
  soNo: string;
  poNo: string;
  meycoNo: string;
  jobName: string;
  orderDate: string;
  shippingDate: string;
  status: string;
  orderType?: "Quote" | "Order" | "Invoice";
  tracking: string;
  isPayButton: boolean;
  allowUpdates?: boolean;
  customer?: any;
}

interface OrderDetailPanelProps {
  record: OrderDetailRecord;
  onPayNow?: () => void;
  onOrderUpdated?: () => void;
}

function paymentStatusColor(status?: string): string {
  const s = (status ?? "").toUpperCase();
  if (s.includes("PAY")) return "gold";
  if (s.includes("PAID") && !s.includes("UNPAID")) return "green";
  return "default";
}

export const OrderDetailPanel: React.FC<OrderDetailPanelProps> = ({
  record,
  onPayNow,
  onOrderUpdated,
}) => {
  const { versions, anchorPlan, loading, error, refetch } = useOrderDetail(
    record.soNo,
  );
  const [activeVersion, setActiveVersion] = useState<OrderVersionDto | null>(
    null,
  );

  const commentInputRef = useRef<CommentResponseInputHandle>(null);
  const commentsSectionRef = useRef<HTMLDivElement>(null);

  const handleRequestComment = (text: string) => {
    commentsSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    commentInputRef.current?.prefill(text);
  };

  const paymentStatus: string | undefined = record.customer?.paymentStatus;
  const currentVersion =
    versions.find((v) => v.versionLabel === activeVersion?.versionLabel) ??
    versions[0];
  const activeComments = currentVersion?.customerComments ?? null;

  if (loading) {
    return <OrderDetailPanelSkeleton />;
  }

  if (error) {
    return (
      <div style={{ padding: "16px 24px" }}>
        <Alert type="error" message={error} />
      </div>
    );
  }
  return (
    <div style={{ padding: "16px 24px" }}>
      <Row gutter={[24, 16]}>
        {/* Left column — pool diagram + order summary */}
        <Col xs={24} lg={7}>
          <AnchorPlanViewer anchorPlan={anchorPlan} />
          <OrderSummaryCard record={record} />
        </Col>

        {/* Right column — header + files + version detail + notes */}
        <Col xs={24} lg={17}>
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 16,
              flexWrap: "wrap",
            }}
          >
            <Typography.Title level={4} style={{ marginBottom: 0, flex: 1 }}>
              {record.jobName || record.soNo} -{" "}
              {record.orderType?.toUpperCase()}
            </Typography.Title>
            {/* <Space>
              {paymentStatus && (
                <Tag color={paymentStatusColor(paymentStatus)}>
                  {paymentStatus.toUpperCase()}
                </Tag>
              )}
              {record.isPayButton && onPayNow && (
                <Button type="primary" size="small" onClick={onPayNow}>
                  Pay Now
                </Button>
              )}
            </Space> */}
          </div>

          {versions.length === 0 ? (
            <Empty description="No data on file" />
          ) : (
            <>
              <QuoteVersionTabs
                versions={versions}
                onActiveVersionChange={setActiveVersion}
                allowUpdates={record.allowUpdates}
                onSaved={() => {
                  refetch();
                  onOrderUpdated?.();
                }}
                onRequestComment={
                  record.allowUpdates ? handleRequestComment : undefined
                }
              />
              <div ref={commentsSectionRef}>
                <ImportantComments
                  ref={commentInputRef}
                  comments={activeComments}
                  soNo={record.soNo}
                  versionLabel={currentVersion?.versionLabel}
                  onNoteAdded={refetch}
                  allowUpdates={record.allowUpdates}
                />
              </div>
            </>
          )}
        </Col>
      </Row>
    </div>
  );
};
